import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import {
  useLogInquiryListStore,
  type LogCategoryFilter,
  type LogSourceFilter,
  type LogStatusFilter,
  type LogUserTypeFilter,
} from '../store/log-inquiry-list.store';
import {
  LOG_CATEGORIES,
  LOG_USER_TYPES,
  type LogCategory,
  type LogSource,
  type LogStatus,
  type LogUserType,
} from '../types/log.types';

// Search is real-time. 300 ms debounce keeps the query rate sane while still
// feeling instant.
const SEARCH_DEBOUNCE_MS = 300;

// SHCT-620 — PRD 18 §4.2: 시작일/종료일 are `YYYY-MM-DD`, i.e. a FOUR-digit year.
// Chrome's date input accepts years up to 275760, so `value` can legitimately
// come back as "19966-01-01" — a valid Date to the browser, but not our format,
// and the API rejects it ("로그를 불러올 수 없습니다"). Guard before dispatching.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// Bounds the native picker to a 4-digit year. Deliberately NOT `max={today}`:
// PRD 18 never restricts log dates to the past (unlike 퇴원일 in PRD 02).
const DATE_INPUT_MIN = '1000-01-01';
const DATE_INPUT_MAX = '9999-12-31';

// Coded option values. The 'all' placeholder is rendered separately via a
// feature t() key; the codes below render through the `labels` catalog.
const SOURCE_CODES: readonly LogSource[] = ['ADMIN', 'MEDICAL_WEB'] as const;
const USER_TYPE_CODES: readonly LogUserType[] = LOG_USER_TYPES;
const CATEGORY_CODES: readonly LogCategory[] = LOG_CATEGORIES;
const STATUS_CODES: readonly LogStatus[] = ['SUCCESS', 'FAIL'] as const;

export function LogInquiryFilters() {
  const { t } = useTranslation('admin');
  // Coded option labels come from the backend label catalog (`labels` ns).
  const { t: tLabels } = useTranslation('labels');
  const filters = useLogInquiryListStore((s) => s.filters);
  const setFilters = useLogInquiryListStore((s) => s.setFilters);

  // ── Search (debounced; real-time behavior) ──────────────────────────
  const [searchLocal, setSearchLocal] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchLocal, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchLocal(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch]);

  // ── Date range (draft state; applies only on [조회] click) ──────────
  const [startDraft, setStartDraft] = useState(filters.dateFrom ?? '');
  const [endDraft, setEndDraft] = useState(filters.dateTo ?? '');
  const [dateError, setDateError] = useState<string | null>(null);
  // PRD 18 §8.1 — a format error belongs under ITS OWN field ("해당 날짜 필드
  // 하단"), unlike the range error, which sits under the 기간 영역 (§8.1 line 396).
  const [startFormatError, setStartFormatError] = useState<string | null>(null);
  const [endFormatError, setEndFormatError] = useState<string | null>(null);

  useEffect(() => {
    setStartDraft(filters.dateFrom ?? '');
    setEndDraft(filters.dateTo ?? '');
  }, [filters.dateFrom, filters.dateTo]);

  const applyDateRange = () => {
    // SHCT-620 — format first. The range check below is a plain string compare,
    // which silently misorders an out-of-format year ('19966-…' < '2026-…'), so
    // a malformed date must never reach it, let alone the API.
    const badStart = Boolean(startDraft) && !ISO_DATE_RE.test(startDraft);
    const badEnd = Boolean(endDraft) && !ISO_DATE_RE.test(endDraft);
    setStartFormatError(badStart ? t('logInquiry.filters.dateFormatError') : null);
    setEndFormatError(badEnd ? t('logInquiry.filters.dateFormatError') : null);
    if (badStart || badEnd) {
      setDateError(null);
      return;
    }
    // dateFrom > dateTo is an inline-error condition.
    if (startDraft && endDraft && startDraft > endDraft) {
      setDateError(t('logInquiry.filters.dateRangeError'));
      return;
    }
    setDateError(null);
    setFilters({
      dateFrom: startDraft || null,
      dateTo: endDraft || null,
    });
  };

  return (
    <>
      {/* Date-range row + [조회] button */}
      <div className="filter-bar" style={{ gap: 8 }}>
        {/* SHCT-620 — `alignItems: flex-start` so a per-field format error can
            grow below its own input without shifting the row's controls. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input
              type="date"
              className={`form-field__input${startFormatError ? ' form-field__input--error' : ''}`}
              style={{ width: 160, height: 36, margin: 0 }}
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              value={startDraft}
              onChange={(e) => setStartDraft(e.target.value)}
              aria-label={t('logInquiry.filters.startDate')}
            />
            {startFormatError && (
              <p className="form-field__error" style={{ margin: 0 }} role="alert">
                {startFormatError}
              </p>
            )}
          </div>
          <span style={{ lineHeight: '36px' }}>~</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input
              type="date"
              className={`form-field__input${endFormatError ? ' form-field__input--error' : ''}`}
              style={{ width: 160, height: 36, margin: 0 }}
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              value={endDraft}
              onChange={(e) => setEndDraft(e.target.value)}
              aria-label={t('logInquiry.filters.endDate')}
            />
            {endFormatError && (
              <p className="form-field__error" style={{ margin: 0 }} role="alert">
                {endFormatError}
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-default btn-primary"
            style={{ height: 36 }}
            onClick={applyDateRange}
          >
            {t('logInquiry.filters.applyDate')}
          </button>
          {dateError && (
            <p
              className="form-field__error"
              style={{ margin: '0 0 0 12px' }}
              role="alert"
            >
              {dateError}
            </p>
          )}
        </div>
      </div>

      {/* Dropdown row — 'all' placeholder via feature t(); codes via catalog */}
      <div className="filter-bar" style={{ gap: 8 }}>
        <select
          className="filter-bar__select"
          style={{ width: 160 }}
          value={filters.source}
          onChange={(e) =>
            setFilters({ source: e.target.value as LogSourceFilter })
          }
        >
          <option value="all">{t('logInquiry.filters.allSource')}</option>
          {SOURCE_CODES.map((code) => (
            <option key={code} value={code}>
              {tLabels(`auditSource.${code}`)}
            </option>
          ))}
        </select>

        <select
          className="filter-bar__select"
          style={{ width: 200 }}
          value={filters.userType}
          onChange={(e) =>
            setFilters({ userType: e.target.value as LogUserTypeFilter })
          }
        >
          <option value="all">{t('logInquiry.filters.allUserType')}</option>
          {USER_TYPE_CODES.map((code) => (
            <option key={code} value={code}>
              {tLabels(`auditUserType.${code}`)}
            </option>
          ))}
        </select>

        <select
          className="filter-bar__select"
          style={{ width: 180 }}
          value={filters.category}
          onChange={(e) =>
            setFilters({ category: e.target.value as LogCategoryFilter })
          }
        >
          <option value="all">{t('logInquiry.filters.allCategory')}</option>
          {CATEGORY_CODES.map((code) => (
            <option key={code} value={code}>
              {tLabels(`auditCategory.${code}`)}
            </option>
          ))}
        </select>

        <select
          className="filter-bar__select"
          style={{ width: 140 }}
          value={filters.status}
          onChange={(e) =>
            setFilters({ status: e.target.value as LogStatusFilter })
          }
        >
          <option value="all">{t('logInquiry.filters.allStatus')}</option>
          {STATUS_CODES.map((code) => (
            <option key={code} value={code}>
              {tLabels(`auditStatus.${code}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Search row */}
      <div className="filter-bar" style={{ gap: 8 }}>
        <div className="filter-bar__search" style={{ flex: 1 }}>
          <input
            type="text"
            className="filter-bar__search-input"
            placeholder={t('logInquiry.filters.searchPlaceholder')}
            value={searchLocal}
            onChange={(e) => setSearchLocal(e.target.value)}
            maxLength={100}
          />
          <Search className="filter-bar__search-icon" size={16} />
        </div>
      </div>
    </>
  );
}
